import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input, Select, TSection as DsTSection, Patterns, NumberInput, Divider } from '@treezor/design-system';

import { UserService } from '@/features/user/services/UserService';
import { CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { Contact } from '@/features/user/components/CreateUser/Contact';
import { useMailInput } from '@/features/user/hooks/createUser/useMailInput';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import { AddressSummary } from '@/features/user/components/CreateUser/AddressSummary';
import { IUser } from '@/features/user/store/user.model';
import { useUserActions } from '@/features/user/hooks/useUserActions';
import { Parent } from '@/features/user/components/Parent';
import { UserContentLabel, UserContentSubSection } from '@/features/user/components/contentLayout';

import { TKycSection } from '@/features/kyc/components/userInformation/InformationSection';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { useUpdateUser } from './useUpdateUser';

interface IPhysicalUserInformationProps {
  user: IUser;
  isPersonalInformationOnEdition: boolean;
  isAddressesOnEdition: boolean;
  isContactOnEdition: boolean;
}

export function usePhysicalUserInformationProps({
  user,
  isPersonalInformationOnEdition,
  isAddressesOnEdition,
  isContactOnEdition,
}: IPhysicalUserInformationProps) {
  const { t } = useTranslation();
  const config = useDashboardConfig();
  const { navigateToUser } = useUserActions();
  const { countryCodeOptions, titleOptions } = useFieldOptions();

  const [parentUser, setParentUser] = useState<IUser>();

  const {
    onUpdateUser,
    handleValueChange,
    register,
    handleInputChange,
    isLoading,
    watch,
    touchedFields,
    inputMaxLength,
    inputMaxNumber,
    hasValidField,
    hasInvalidInputLength,
    onCancelEdition,
  } = useUpdateUser(user);

  const distributionCountry = config?.settings?.users?.distributionCountry;
  const hasDistributionCountry = distributionCountry?.length > 0;

  const emailWatch = watch('email');
  const lastNameWatch = watch('firstname');
  const firstNameWatch = watch('firstname');
  const distributionCountryWatch = watch('distributionCountry');

  const address1Watch = watch('address1');
  const address2Watch = watch('address2');
  const address3Watch = watch('address3');
  const secondaryAddress1Watch = watch('secondaryAddress1');
  const secondaryAddress2Watch = watch('secondaryAddress2');
  const secondaryAddress3Watch = watch('secondaryAddress3');

  const phoneWatch = watch('phone');
  const mobileWatch = watch('mobile');
  const effectiveWatch = watch('effectiveBeneficiary');
  const role = DashboardHelpers.getUserRole(user);
  const hasEffectiveBeneficiary = !!user.effectiveBeneficiary;

  const {
    iconStatus,
    error,
    handleAsyncBlur: handleEmailBlur,
  } = useMailInput({ initialValue: emailWatch, isRequired: true });

  const emailError = useMemo(() => (user.email !== emailWatch ? error : undefined), [emailWatch, error, user.email]);

  const isPersonalInformationInvalid = useMemo(
    () =>
      !emailWatch ||
      !!emailError ||
      !lastNameWatch ||
      !firstNameWatch ||
      (hasEffectiveBeneficiary &&
        (Number(effectiveWatch) === 0 || Number(effectiveWatch) > 100 || isNaN(Number(effectiveWatch)))) ||
      (hasDistributionCountry && !distributionCountryWatch),
    [
      emailWatch,
      emailError,
      lastNameWatch,
      firstNameWatch,
      hasEffectiveBeneficiary,
      effectiveWatch,
      hasDistributionCountry,
      distributionCountryWatch,
    ],
  );

  const isContactInvalid = useMemo(
    () => (!!mobileWatch && !Patterns.phone.test(mobileWatch)) || (!!phoneWatch && !Patterns.phone.test(phoneWatch)),
    [mobileWatch, phoneWatch],
  );

  const isAddressValid = useMemo(
    () =>
      hasInvalidInputLength(['address1', 'address2', 'address3']) ||
      hasInvalidInputLength(['secondaryAddress1', 'secondaryAddress2', 'secondaryAddress3']),
    [hasInvalidInputLength],
  );

  useEffect(() => {
    async function fetchData() {
      if (user.parentUserId) {
        const parentUser = await UserService.searchUsers({ userId: user.parentUserId });
        setParentUser(parentUser?.[0] ?? undefined);
      }
    }
    fetchData();
  }, [user.parentUserId, user.userId]);

  const hierarchicalRelation: DsTSection[] = !!parentUser
    ? [
        {
          subTitle: t('pages.dashboard.createUser.form.blocks.hierarchicalRelation'),
          categories: [
            {
              component: <Parent redirectToParent={() => navigateToUser(user.parentUserId)} parentUser={parentUser} />,
            },
          ],
        },
      ]
    : [];

  const personalInformation: TKycSection = {
    subTitle: t('pages.dashboard.createUser.form.blocks.mainInformation'),
    ...(isPersonalInformationOnEdition
      ? {
          component: (
            <div className="flex flex-col gap-3">
              <CustomColumns columns={[1, 2]}>
                <UserContentLabel>{t('pages.dashboard.createUser.form.title')}</UserContentLabel>
                <Select
                  options={titleOptions}
                  defaultValue={user?.title}
                  onChange={(value: string) => handleValueChange(value, 'title')}
                  showDelete
                  size="s"
                  testId="select-kyc-title"
                />
              </CustomColumns>
              <CustomColumns columns={[1, 2]}>
                <UserContentLabel>{t('pages.dashboard.createUser.form.lastname')}</UserContentLabel>
                <Input
                  type="text"
                  label=""
                  required
                  size="s"
                  {...register('lastname')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'lastname')
                  }
                  testId="input-kyc-lastname"
                />
              </CustomColumns>
              <CustomColumns columns={[1, 2]}>
                <UserContentLabel>{t('pages.dashboard.createUser.form.firstname')}</UserContentLabel>
                <Input
                  type="text"
                  required
                  label=""
                  size="s"
                  {...register('firstname')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'firstname')
                  }
                  testId="input-kyc-firstname"
                />
              </CustomColumns>
              <AdditonalDataLayout
                shouldShow={true}
                label={t('pages.dashboard.createUser.form.actions.addMiddleNames')}
              >
                <CustomColumns columns={[1, 2]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.middleName')}</UserContentLabel>
                  <CustomColumns columns={[1, 1]} className="gap-3">
                    <Input
                      type="text"
                      label=""
                      size="s"
                      {...register('middleName1')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'middleName1')
                      }
                      testId="input-kyc-middle-name-1"
                    />
                    <Input
                      type="text"
                      label=""
                      size="s"
                      {...register('middleName2')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'middleName2')
                      }
                      testId="input-kyc-middle-name-2"
                    />
                  </CustomColumns>
                </CustomColumns>
              </AdditonalDataLayout>
              <CustomColumns columns={[1, 2]}>
                <UserContentLabel>{t('pages.dashboard.createUser.form.email')}</UserContentLabel>
                <Input
                  type="text"
                  icon={{ left: 'mail', right: iconStatus }}
                  label=""
                  required
                  {...register('email', { pattern: Patterns.email })}
                  error={emailError}
                  onBlur={handleEmailBlur}
                  size="s"
                  testId="input-kyc-email"
                />
              </CustomColumns>
              {!!hasEffectiveBeneficiary && (
                <CustomColumns columns={[1, 2]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.role')}</UserContentLabel>
                  <CustomColumns columns={[2, 1]} className="items-center gap-3">
                    <span>{t(`pages.dashboard.users.physicalUserRole.${role}`)}</span>
                    <NumberInput
                      label=""
                      defaultValue={effectiveWatch}
                      unit={
                        <div className="flex gap-2 items-center h-[20px]">
                          <Divider direction="vertical" />
                          <div>%</div>
                        </div>
                      }
                      size="s"
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleValueChange(event.currentTarget.value, 'effectiveBeneficiary')
                      }
                      {...inputMaxNumber(effectiveWatch, 100)}
                    />
                  </CustomColumns>
                </CustomColumns>
              )}

              {hasDistributionCountry && (
                <CustomColumns columns={[1, 2]}>
                  <UserContentLabel>
                    {t('pages.currentProfiles.profile.informations.distributionCountry')}
                  </UserContentLabel>
                  <Select
                    required
                    withSearch
                    options={countryCodeOptions.filter(({ value }) => distributionCountry.includes(value))}
                    defaultValue={user?.distributionCountry}
                    noOptionsComponent={<CreateUserEmptyComponent />}
                    size="s"
                    onChange={(value: string) => handleValueChange(value, 'distributionCountry')}
                    testId="input-kyc-email"
                  />
                </CustomColumns>
              )}
            </div>
          ),
        }
      : {
          categories: [
            {
              items: [
                {
                  label: t('pages.dashboard.createUser.form.title'),
                  value: user.title ? t(`pages.dashboard.users.titles.${user.title}`) : '/',
                },
                { label: t('pages.dashboard.createUser.form.lastname'), value: user.lastname },
                {
                  label: t('pages.dashboard.createUser.form.firstname'),
                  value: [user.firstname, ...user.middleNames.split(', ')].filter(Boolean).join(', '),
                },
                { label: t('pages.dashboard.createUser.form.email'), value: user.email },
                ...(!!role
                  ? [
                      {
                        label: t('pages.dashboard.createUser.form.role'),
                        value: `${t(`pages.dashboard.users.physicalUserRole.${role}`)} ${hasEffectiveBeneficiary ? `${user.effectiveBeneficiary}%` : ''}`,
                      },
                    ]
                  : []),
                ...(config?.settings?.users?.distributionCountry?.length > 0
                  ? [
                      {
                        label: t('pages.currentProfiles.profile.informations.distributionCountry'),
                        value: user.distributionCountry ? t(`shared.countries.${user.distributionCountry}`) : '/',
                      },
                    ]
                  : []),
              ],
            },
          ],
        }),
  };

  const address: TKycSection = {
    subTitle: t('pages.dashboard.createUser.form.blocks.address'),

    ...(isAddressesOnEdition
      ? {
          component: (
            <div className="flex flex-col gap-4">
              <UserContentSubSection title={t('pages.dashboard.createUser.form.mainAddress')}>
                <Select
                  label={t('pages.currentProfiles.profile.contact.country')}
                  options={countryCodeOptions}
                  withSearch
                  defaultValue={user?.country}
                  onChange={(value: string) => {
                    handleValueChange(value, 'country');
                  }}
                  noOptionsComponent={<CreateUserEmptyComponent />}
                  showDelete
                  size="s"
                  testId="select-kyc-country"
                />
                <CustomColumns columns={[1, 1]} className="gap-5">
                  <Input
                    value={address1Watch}
                    type="text"
                    label={t('pages.dashboard.createUser.form.address1')}
                    {...register('address1')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'address1')
                    }
                    {...inputMaxLength(address1Watch, 35)}
                    size="s"
                    testId="select-kyc-address1"
                  />
                  <Input
                    value={address2Watch}
                    type="text"
                    label={t('pages.dashboard.createUser.form.address2')}
                    {...register('address2')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'address2')
                    }
                    {...inputMaxLength(address2Watch, 35)}
                    size="s"
                    testId="select-kyc-address2"
                  />
                </CustomColumns>
                <Input
                  value={address3Watch}
                  type="text"
                  label={t('pages.dashboard.createUser.form.address3')}
                  {...register('address3')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'address3')
                  }
                  {...inputMaxLength(address3Watch, 35)}
                  size="s"
                  testId="select-kyc-address3"
                />
                <CustomColumns columns={[1, 1]} className="gap-5 items-start">
                  <Input
                    type="text"
                    label={t('pages.currentProfiles.profile.contact.postcode')}
                    {...register('postcode')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'postcode')
                    }
                    defaultValue={user?.postcode}
                    size="s"
                    testId="select-kyc-postcode"
                  />
                  <Input
                    type="text"
                    label={t('pages.currentProfiles.profile.contact.city')}
                    {...register('city')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'city')
                    }
                    size="s"
                    testId="input-kyc-city"
                  />
                </CustomColumns>
              </UserContentSubSection>
              <AdditonalDataLayout
                shouldShow={hasValidField([
                  'secondaryAddress1',
                  'secondaryAddress2',
                  'secondaryAddress3',
                  'secondaryPostcode',
                  'secondaryCity',
                  'secondaryCountry',
                ])}
                label={t('pages.dashboard.createUser.form.actions.addSecondaryAddress')}
              >
                <UserContentSubSection title={t('pages.dashboard.createUser.form.secondaryAddress')}>
                  <Select
                    label={t('pages.currentProfiles.profile.contact.country')}
                    options={countryCodeOptions}
                    withSearch
                    defaultValue={user?.secondaryCountry}
                    onChange={(value: string) => handleValueChange(value, 'secondaryCountry')}
                    noOptionsComponent={<CreateUserEmptyComponent />}
                    showDelete
                    size="s"
                    testId="select-kyc-country"
                  />
                  <CustomColumns columns={[1, 1]} className="gap-5 items-start">
                    <Input
                      value={secondaryAddress1Watch}
                      type="text"
                      label={t('pages.dashboard.createUser.form.address1')}
                      {...register('secondaryAddress1')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'secondaryAddress1')
                      }
                      {...inputMaxLength(secondaryAddress1Watch, 35)}
                      size="s"
                      testId="input-kyc-secondary-address1"
                    />
                    <Input
                      type="text"
                      value={secondaryAddress2Watch}
                      label={t('pages.dashboard.createUser.form.address2')}
                      {...register('secondaryAddress2')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'secondaryAddress2')
                      }
                      {...inputMaxLength(secondaryAddress2Watch, 35)}
                      size="s"
                      testId="input-kyc-secondary-address2"
                    />
                  </CustomColumns>
                  <Input
                    type="text"
                    value={secondaryAddress3Watch}
                    label={t('pages.dashboard.createUser.form.address3')}
                    {...register('secondaryAddress3')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'secondaryAddress3')
                    }
                    {...inputMaxLength(secondaryAddress3Watch, 35)}
                    size="s"
                    testId="input-kyc-secondary-address3"
                  />
                  <CustomColumns columns={[1, 1]} className="gap-5 items-start">
                    <Input
                      type="text"
                      label={t('pages.currentProfiles.profile.contact.postcode')}
                      {...register('secondaryPostcode')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'secondaryPostcode')
                      }
                      defaultValue={user?.secondaryPostcode}
                      size="s"
                      testId="input-kyc-secondary-postcode"
                    />
                    <Input
                      type="text"
                      label={t('pages.currentProfiles.profile.contact.city')}
                      {...register('secondaryCity')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'secondaryCity')
                      }
                      size="s"
                      testId="input-kyc-secondary-city"
                    />
                  </CustomColumns>
                </UserContentSubSection>
              </AdditonalDataLayout>
            </div>
          ),
        }
      : {
          categories: [
            {
              component: (
                <div className="flex gap-8">
                  <AddressSummary
                    isPrimary
                    title={t('pages.dashboard.createUser.form.mainAddress')}
                    address1={user.address1}
                    address2={user.address2}
                    address3={user.address3}
                    postcode={user.postcode}
                    city={user.city}
                    country={user.country}
                  />

                  <AddressSummary
                    title={t('pages.dashboard.createUser.form.secondaryAddress')}
                    address1={user.secondaryAddress1}
                    address2={user.secondaryAddress2}
                    address3={user.secondaryAddress3}
                    postcode={user.secondaryPostcode}
                    city={user.secondaryCity}
                    country={user.secondaryCountry}
                  />
                </div>
              ),
            },
          ],
        }),
  };

  const contact: TKycSection = {
    subTitle: t('pages.dashboard.createUser.form.blocks.contact'),
    ...(isContactOnEdition
      ? {
          component: (
            <div className="flex flex-col gap-3">
              <CustomColumns columns={[1, 2]}>
                <UserContentLabel>{t('pages.dashboard.createUser.form.phone')}</UserContentLabel>
                <Contact
                  label=""
                  value={phoneWatch}
                  initialValue={user?.phone}
                  initialCountry={!touchedFields?.['phone'] && !phoneWatch ? distributionCountryWatch : undefined}
                  handleValueChange={handleValueChange}
                  size="s"
                  {...register('phone')}
                  testId="input-kyc-phone"
                />
              </CustomColumns>
              <CustomColumns columns={[1, 2]}>
                <UserContentLabel>{t('pages.dashboard.createUser.form.mobile')}</UserContentLabel>
                <Contact
                  label=""
                  value={mobileWatch}
                  initialValue={user?.mobile}
                  initialCountry={!touchedFields?.['mobile'] && !mobileWatch ? distributionCountryWatch : undefined}
                  handleValueChange={handleValueChange}
                  size="s"
                  {...register('mobile')}
                  testId="input-kyc-mobile"
                />
              </CustomColumns>
            </div>
          ),
        }
      : {
          categories: [
            {
              items: [
                { label: t('pages.dashboard.createUser.form.phone'), value: user?.phone || '/' },
                { label: t('pages.dashboard.createUser.form.mobile'), value: user?.mobile || '/' },
              ],
            },
          ],
        }),
  };

  return {
    hierarchicalRelation,
    personalInformation,
    address,
    contact,
    onUpdateUser,
    isLoading,
    isPersonalInformationInvalid,
    isAddressValid,
    isContactInvalid,
    onCancelEdition,
  };
}
